import React, { useState, useMemo } from 'react';
import { FileSignature, TrendingUp, AlertTriangle, Calendar } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';

export function BallotInitiativeForecaster() {
  const [legalRequirement, setLegalRequirement] = useState<number>(150000);
  const [safeBufferPercent, setSafeBufferPercent] = useState<number>(30);
  const [collectedSoFar, setCollectedSoFar] = useState<number>(65000);
  const [daysRemaining, setDaysRemaining] = useState<number>(45);

  const model = useMemo(() => {
    // Total target includes the legal requirement + the safe buffer to account for rejected signatures
    const bufferAmount = Math.ceil(legalRequirement * (safeBufferPercent / 100));
    const totalTarget = legalRequirement + bufferAmount;
    
    const remainingToTarget = Math.max(0, totalTarget - collectedSoFar);
    
    // Required daily run rate to hit the TARGET
    const requiredDailyRunRate = daysRemaining > 0 ? Math.ceil(remainingToTarget / daysRemaining) : 0;
    
    // Current progress
    const progressPercent = Math.min(100, (collectedSoFar / totalTarget) * 100);
    const hasHitLegalMin = collectedSoFar >= legalRequirement;
    const hasHitTarget = collectedSoFar >= totalTarget;

    return {
      bufferAmount,
      totalTarget,
      remainingToTarget,
      requiredDailyRunRate,
      progressPercent,
      hasHitLegalMin,
      hasHitTarget
    };
  }, [legalRequirement, safeBufferPercent, collectedSoFar, daysRemaining]);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-emerald-700 to-teal-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <FileSignature className="w-6 h-6 mr-3 text-emerald-300" />
            Ballot Initiative Forecaster
          </h2>
          <p className="text-emerald-100 max-w-xl">
            Never fail at the Secretary of State's office due to signature invalidation. Calculate your required daily run-rate to hit your legal minimums plus a safe validity buffer.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Controls */}
        <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
              Legal Minimum Signatures
            </label>
            <Input 
              type="number" 
              value={legalRequirement} 
              onChange={(e) => setLegalRequirement(Number(e.target.value))}
              className="text-lg font-bold"
            />
          </div>

          <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
            <label className="text-sm font-semibold text-slate-500 uppercase flex justify-between mb-2">
              <span>Safety Buffer</span>
              <span className="font-bold text-slate-900 dark:text-white">{safeBufferPercent}%</span>
            </label>
            <input 
              type="range" 
              min="0" 
              max="50" 
              value={safeBufferPercent} 
              onChange={(e) => setSafeBufferPercent(Number(e.target.value))}
              className="w-full accent-emerald-600"
            />
            <p className="text-xs text-slate-400 mt-2">Recommended 20-30% to account for duplicate or unregistered voter signatures being struck down.</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Collected So Far</label>
              <Input 
                type="number" 
                value={collectedSoFar} 
                onChange={(e) => setCollectedSoFar(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2 flex items-center">
                <Calendar className="w-4 h-4 mr-1 text-emerald-500" /> Days Left
              </label>
              <Input 
                type="number" 
                value={daysRemaining} 
                onChange={(e) => setDaysRemaining(Number(e.target.value))}
              />
            </div>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className={`p-6 border-slate-200 dark:border-slate-800 shadow-xl text-white ${model.hasHitTarget ? 'bg-emerald-700' : 'bg-slate-900'}`}>
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Required Daily Run-Rate</h3>
              {model.hasHitTarget ? <FileSignature className="w-8 h-8 text-emerald-300" /> : <TrendingUp className="w-8 h-8 text-emerald-500" />}
            </div>
            
            <div className="text-5xl font-black tracking-tight mb-2">
              {model.hasHitTarget ? 'DONE' : model.requiredDailyRunRate.toLocaleString()} <span className="text-lg font-normal text-slate-400">/ day</span>
            </div>
            <p className="text-slate-400 text-sm">
              Must collect this many signatures every day for the next {daysRemaining} days to hit your safe target.
            </p>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex justify-between text-xs font-bold uppercase tracking-wider mb-2">
                <span className="text-emerald-400">Progress: {model.progressPercent.toFixed(1)}%</span>
                <span className="text-slate-500">Target: {model.totalTarget.toLocaleString()}</span>
              </div>
              <div className="h-4 bg-slate-800 rounded-full overflow-hidden border border-slate-700 relative">
                {/* Legal min marker */}
                <div 
                  className="absolute top-0 bottom-0 w-0.5 bg-yellow-500 z-10" 
                  style={{ left: `${(legalRequirement / model.totalTarget) * 100}%` }}
                  title="Legal Minimum"
                />
                <div 
                  className="h-full bg-emerald-500 transition-all duration-500" 
                  style={{ width: `${model.progressPercent}%` }}
                />
              </div>
              {!model.hasHitLegalMin && (
                <div className="mt-2 text-xs text-yellow-500 flex items-center">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Still { (legalRequirement - collectedSoFar).toLocaleString() } short of legal minimum
                </div>
              )}
            </div>
          </Card>

          <Card className="p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Legal Minimum</div>
                  <div className="text-sm text-slate-500">Required by state law</div>
                </div>
                <div className="text-xl font-bold">
                  {legalRequirement.toLocaleString()}
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between bg-emerald-50 dark:bg-emerald-900/10">
                <div>
                  <div className="font-bold text-emerald-800 dark:text-emerald-300">Safety Buffer ({safeBufferPercent}%)</div>
                  <div className="text-sm text-emerald-600 dark:text-emerald-400">Extra padding for invalidated signatures</div>
                </div>
                <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  +{model.bufferAmount.toLocaleString()}
                </div>
              </div>

              <div className="p-4 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Total Safe Target</div>
                  <div className="text-sm text-slate-500">The actual number you should aim for</div>
                </div>
                <div className="text-2xl font-black text-slate-900 dark:text-white">
                  {model.totalTarget.toLocaleString()}
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
